import { computed, inject, provide, toValue, unref } from 'vue';
import { isNil } from 'lodash';

import type { InjectionKey, MaybeRefOrGetter, Ref } from 'vue';
import type { CustomFormsStorageContext } from '../components/custom_forms/types';

/**
 * The authenticated endpoint, used by every app that uploads as a signed-in user. Public flows
 * (Juno's checkout) pass their own public path instead, which DocumentUploadButton rewrites to
 * carry the warehouse id.
 */
const DEFAULT_STORAGE_ENDPOINT = 'storage';

/**
 * Injection key for custom forms document upload context.
 * Apps that render document/multi-document custom form fields must provide
 * this so uploads can target the correct storage endpoint.
 * Can provide a plain object or a Ref for reactivity.
 *
 * @example
 * ```ts
 * // In app (e.g. Juno CheckoutDetailsPage)
 * import { CUSTOM_FORMS_STORAGE_CONTEXT_KEY } from '@satellite/composables/useCustomFormsStorageContext';
 *
 * const storageContext = computed(() => ({
 *   warehouseId: warehouse.value?.id ?? '',
 *   storageEndpoint: 'checkout/public/storage'
 * }));
 * provide(CUSTOM_FORMS_STORAGE_CONTEXT_KEY, storageContext);
 * ```
 */
export const CUSTOM_FORMS_STORAGE_CONTEXT_KEY: InjectionKey<
  CustomFormsStorageContext | Ref<CustomFormsStorageContext>
> = Symbol('customFormsStorageContext');

/**
 * Provides the storage context for any custom form field rendered below this component.
 *
 * Document and multi-document fields inject it to target the right upload endpoint, and the
 * injection throws when it is missing — from inside a computed, so the failure lands on the first
 * render of a document field rather than at setup. Any component that renders custom fields
 * dynamically therefore has to provide it, since the field type comes from warehouse config and
 * cannot be ruled out by reading the template.
 *
 * @param warehouseId - The warehouse owning the uploads. Pass a getter or ref when it arrives
 *   after mount (an appointment loading in, a prop changing) so uploads follow it.
 * @param storageEndpoint - Defaults to the authenticated endpoint; pass a public path for
 *   unauthenticated flows.
 *
 * @example
 * ```ts
 * provideCustomFormsStorageContext(() => props.warehouse?.id);
 * ```
 */
export function provideCustomFormsStorageContext(
  warehouseId: MaybeRefOrGetter<string | undefined>,
  storageEndpoint: MaybeRefOrGetter<string> = DEFAULT_STORAGE_ENDPOINT
): void {
  provide(
    CUSTOM_FORMS_STORAGE_CONTEXT_KEY,
    computed(() => ({
      warehouseId: toValue(warehouseId) ?? '',
      storageEndpoint: toValue(storageEndpoint)
    }))
  );
}

/**
 * Injects the custom forms storage context for document upload fields.
 * Throws if the app has not provided context or the context is invalid
 * (missing warehouseId or storageEndpoint). When provided value is a Ref,
 * returns a computed that stays reactive.
 */
export function useCustomFormsStorageContext(): Ref<CustomFormsStorageContext> {
  const injected = inject(CUSTOM_FORMS_STORAGE_CONTEXT_KEY);

  return computed(() => {
    if (isNil(injected)) {
      throw new Error(
        'Custom forms storage context was not provided. Provide CUSTOM_FORMS_STORAGE_CONTEXT_KEY with warehouseId and storageEndpoint when using document upload fields.'
      );
    }

    const ctx = unref(injected);
    if (!ctx?.warehouseId || !ctx?.storageEndpoint) {
      throw new Error(
        'Custom forms storage context is invalid: warehouseId and storageEndpoint are required.'
      );
    }

    return ctx;
  });
}
